<template>
  <div class="scan-login-container">
    <div class="qrcode-wrapper">
      <!-- Loading State -->
      <div v-if="loading" class="state-layer loading">
        <div class="spinner"></div>
      </div>

      <!-- QR Code Image -->
      <img 
        v-if="qrCodeUrl" 
        :src="qrCodeUrl" 
        class="qrcode-img"
        alt="Scan to Login"
      />

      <!-- Overlays for different states -->
      <div v-if="status !== 'unlogin'" class="overlay">
        
        <!-- Expired State -->
        <div v-if="status === 'imgExpired'" class="overlay-content" @click="refreshCode">
          <span class="status-text warning">二维码已过期</span>
          <span class="action-text">点击刷新</span>
        </div>

        <!-- Failed State -->
        <div v-if="status === 'loginFailed'" class="overlay-content" @click="refreshCode">
          <span class="status-text error">登录失败</span>
          <span class="action-text">点击重试</span>
        </div>

        <!-- Success State -->
        <div v-if="status === 'loginSuccess'" class="overlay-content">
          <span class="status-text success">登录成功</span>
          <span class="action-text">正在跳转...</span>
        </div>
      </div>
    </div>

    <!-- Bottom Hint Text -->
    <div class="hint-text">{{ hintText }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { authApi, AuthErrors } from '@/api/auth'

const emit = defineEmits(['success', 'failed'])

// Constants
const STATUS = {
  UNLOGIN: 'unlogin',
  EXPIRED: 'imgExpired',
  SUCCESS: 'loginSuccess',
  FAILED: 'loginFailed'
}

// State
const status = ref(STATUS.UNLOGIN)
const qrCodeUrl = ref('')
const loading = ref(false)
const scene = ref('')
let pollTimer = null
let pollCount = 0
const MAX_POLL_COUNT = 60 // 2s * 60 = 120s timeout

// Computed Hint Text
const hintText = computed(() => {
  switch (status.value) {
    case STATUS.EXPIRED: return '二维码已过期，请点击刷新'
    case STATUS.SUCCESS: return '登录成功'
    case STATUS.FAILED: return '登录失败，请重试'
    default: return '微信扫码登录'
  }
})

// Actions
const clearTimer = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const startPolling = () => {
  clearTimer()
  pollCount = 0
  
  pollTimer = setInterval(async () => {
    // Stop if max count reached
    if (pollCount >= MAX_POLL_COUNT) {
      status.value = STATUS.EXPIRED
      clearTimer()
      return
    }

    try {
      // Pass true to request share_code
      const res = await authApi.getLoginStatus(scene.value, true)
      
      // Case 1: Success (Has Data)
      if (res.data) {
        status.value = STATUS.SUCCESS
        localStorage.setItem('loginToken', JSON.stringify(res.data))
        clearTimer()
        // Wait a bit for UI feedback
        setTimeout(() => {
          emit('success', res.data)
        }, 500)
        return
      }

      // Case 2: Specific Status Codes
      if (res.status === AuthErrors.ErrorMchatNotLogin) {
        // Still waiting for user to scan
        pollCount++
        return
      }

      if (res.status === AuthErrors.ErrorMchatExpiredScene) {
        status.value = STATUS.EXPIRED
        clearTimer()
        return
      }

      if (res.status === AuthErrors.ErrorMchatInvalidScene) {
        status.value = STATUS.FAILED
        emit('failed')
        clearTimer()
        return
      }

      // Case 3: Unknown Error
      if (res.status === undefined) {
          status.value = STATUS.FAILED
          clearTimer()
      }

    } catch (e) {
      console.error('Poll error', e)
      status.value = STATUS.FAILED
      clearTimer()
    }
  }, 2000)
}

const initLogin = async () => {
  loading.value = true
  status.value = STATUS.UNLOGIN
  clearTimer()

  try {
    const res = await authApi.getLoginScene()
    if (res.status === 0 && res.data && res.data.scene) {
      scene.value = res.data.scene
      qrCodeUrl.value = res.data.qr_url ?? authApi.getQrCodeUrl(scene.value)
      startPolling()
    } else {
      status.value = STATUS.FAILED
    }
  } catch (e) {
    console.error('Init login error', e)
    status.value = STATUS.FAILED
  } finally {
    loading.value = false
  }
}

const refreshCode = () => {
  initLogin()
}

// Lifecycle
onMounted(() => {
  initLogin()
})

onUnmounted(() => {
  clearTimer()
})
</script>

<style scoped>
.scan-login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.qrcode-wrapper {
  position: relative;
  width: 180px;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qrcode-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.state-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  justify-content: center;
}

.status-text {
  font-size: 14px;
  font-weight: 600;
}

.status-text.warning { color: #facc15; }
.status-text.error { color: #f87171; }
.status-text.success { color: #4ade80; }

.action-text {
  color: #fff;
  font-size: 12px;
  opacity: 0.9;
}

.hint-text {
  margin-top: 16px;
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

/* Simple Spinner */
.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

